<script setup>
// One set of booking rules as a row of the Bookings page's list. X83 (Grant
// 2026-10-20: "the list cells are far too busy … more important data is how
// many playing areas, and probably which ones"): the row is quiet: the name,
// how many playing areas and which ("Court 1, Court 2, Court 3 and 1 more";
// the whole list in a tooltip when cut; "2 of 4 can be booked" when some are
// walk-up), one hours line (the API's hoursLine: "Every day 8am–10pm",
// "Mon–Fri 5pm–10pm · Sat–Sun 9am–6pm"), the X72 "N day changes coming up"
// link, the attention chip when needed, and a pencil (on every row, X84). The rule chips, the
// shared-rules line and the week bar are gone from the list: the rules live on
// the set's own page, which the whole row opens (the only editor). A set that
// needs attention shows its name, a warning chip, one sentence and the one
// action instead.
import {computed} from 'vue';
import TlIconBtn from '~/components/admin/TlIconBtn.vue';
import {areasLine, groupAttention} from '~/utils/bookings';
import {dayChangesLine, dayChangesQuery} from '~/utils/bookingDayOverrides';

const props = defineProps({
  group: {type: Object, required: true},
  clubId: {type: String, required: true},
  // X230: false when the club has no playing areas at all, so an empty set isn't flagged.
  clubHasAreas: {type: Boolean, default: true},
});
// X96: {action: 'paint' | 'bookable', group}: the page opens the fix's dialog in place.
const emit = defineEmits(['fix']);

const router = useRouter();
const editRoute = computed(() => ({name: 'ClubBookingGroup', params: {clubId: props.clubId, id: props.group.id}}));
const open = () => router.push(editRoute.value);
const attention = computed(() => groupAttention(props.group, props.clubId, {clubHasAreas: props.clubHasAreas}));
const areas = computed(() => areasLine(props.group));
const hasHours = computed(() => !!(props.group.windows ?? []).length);
const hoursLine = computed(() => hasHours.value ? props.group.hoursLine : 'No booking hours yet: every hour is open play');
// X72: "2 day changes coming up", a link to the calendar tab on the first changed day.
const dayChanges = computed(() => dayChangesLine(props.group.dayOverridesComingUp ?? props.group.upcomingDayOverrides?.length ?? 0));
const dayChangesRoute = computed(() => ({name: 'ClubBookings', params: {clubId: props.clubId}, query: {tab: 'calendar', ...dayChangesQuery(props.group)}}));
</script>

<template>
  <div class="gl-row" :class="{'gl-row--attention': attention}" role="link" tabindex="0" @click="open" @keydown.enter="open">
    <div class="gl-who">
      <div class="gl-name-line">
        <nuxt-link class="gl-name" :to="editRoute" @click.stop>{{ group.name }}</nuxt-link>
        <span v-if="attention" class="tl-chip tl-chip--warn"><v-icon size="13">mdi-alert-outline</v-icon>{{ attention.text }}</span>
      </div>
      <div v-if="attention" class="gl-sub">{{ attention.help }}</div>
      <template v-else>
        <div class="gl-sub gl-areas">
          <!-- X84 (R30 L3): each part carries its own " · " on one line, so no template whitespace doubles a space -->
          <span class="gl-count">{{ areas.count }}</span><span v-if="areas.names" class="gl-names">{{ ' · ' + areas.names }}<v-tooltip v-if="areas.cut" activator="parent" location="top" :text="areas.all"/></span><span v-if="areas.bookable" class="gl-bookable">{{ ' · ' + areas.bookable }}</span>
        </div>
        <div class="gl-sub gl-hours"><v-icon size="12">mdi-clock-outline</v-icon>{{ hoursLine }}</div>
        <div v-if="dayChanges" class="gl-sub gl-changes">
          <v-icon size="12">mdi-calendar-edit</v-icon>
          <nuxt-link :to="dayChangesRoute" @click.stop>{{ dayChanges }}</nuxt-link>
        </div>
      </template>
      <!-- X96: a fix with a dialog (paint the hours, choose which can be booked) opens it on this tab; "Open the rules" opens the set -->
      <v-btn v-if="attention && attention.action" class="gl-fix" size="small" variant="outlined" color="primary" @click.stop="emit('fix', {action: attention.action, group})">{{ attention.link }}</v-btn>
      <v-btn v-else-if="attention" class="gl-fix" size="small" variant="outlined" color="primary" :to="attention.route" @click.stop>{{ attention.link }}</v-btn>
    </div>

    <div class="gl-act" @click.stop>
      <!-- X84 (R30): the pencil stays on a row that needs attention too, beside its "Open the rules" -->
      <div class="tl-rowactions gl-pencil">
        <tl-icon-btn tooltip="Edit these booking rules" icon="mdi-pencil" @click="open"/>
      </div>
      <v-icon class="gl-chevron" size="18">mdi-chevron-right</v-icon>
    </div>
  </div>
</template>

<style scoped lang="scss">
/* the row: the words, then the pencil */
.gl-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 40px;
  gap: 6px 16px;
  align-items: center;
  padding: 10px 16px;
  min-height: 56px;
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));
  cursor: pointer;
  outline: none;

  &:last-child {
    border-bottom: 0;
  }

  &:hover,
  &:focus-visible {
    background: rgb(var(--v-theme-light));
  }

  /* a set that needs attention: an amber edge, like the applications banner, not a tinted row */
  &.gl-row--attention {
    box-shadow: inset 3px 0 0 rgb(var(--v-theme-warning));
  }
}

.gl-who {
  min-width: 0;
}

.gl-name-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
}

.gl-name {
  font-size: 13.5px;
  font-weight: 700;
  color: rgb(var(--v-theme-text));
  text-decoration: none;
  letter-spacing: -0.1px;

  &:hover {
    color: rgb(var(--v-theme-primary));
  }
}

.gl-sub {
  font-size: 12px;
  line-height: 1.5;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 2px;
}

/* "4 playing areas · Court 1, Court 2, Court 3 and 1 more · 2 of 4 can be booked" */
.gl-count {
  font-weight: 600;
  color: rgb(var(--v-theme-charcoal));
  white-space: nowrap;
}

.gl-names {
  margin-left: 0;
}

.gl-bookable {
  white-space: nowrap;
}

.gl-hours,
.gl-changes {
  display: flex;
  align-items: center;
  gap: 5px;
}

/* X72: the day changes line, a link to the calendar on the first changed day */
.gl-changes {
  color: rgb(var(--v-theme-warning));

  a {
    color: inherit;
    font-weight: 600;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

.gl-fix {
  margin-top: 8px;
}

.gl-act {
  display: flex;
  justify-content: flex-end;
  align-self: center;
}

.gl-chevron {
  display: none;
  color: rgb(var(--v-theme-textSubtle));
}

/* phones: a chevron instead of the pencil */
@media (max-width: 700px) {
  .gl-row {
    grid-template-columns: minmax(0, 1fr) 20px;
    gap: 8px 10px;
    padding: 12px 16px;
  }

  .gl-pencil {
    display: none;
  }

  .gl-chevron {
    display: inline-flex;
  }
}
</style>
